import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { DropDownItem } from '../../../../models/shared/stylesheet/drop-down-item';
import { SpousalWaiverAttachment } from '../../../../models/submissions/spousal-waiver-attachment';
import { SubmissionAttachment } from '../../../../models/submissions/submission-attachment';
import {
  buildSpousalWaiverEntryFormGroup,
  JurisdictionSpousalWaiverFormGroup,
  SpousalWaiverEntryFormGroup
} from '../../../../forms/spousal-waiver-form-group';
import { GOVERNING_JURISDICTION_NAMES } from '../../../../consts/consts';
import { GoverningJurisdictionTypeId } from '../../../../models/enum/shared/governing-jurisdiction-type-id';
import { Attachment } from '../../../../models/submissions/attachment';
import { TypeService } from '../../../../services/type/type-service';
import { DropdownInputComponent } from '../../../../ui/form-components/dropdown-input/dropdown-input.component';
import { DatepickerInputComponent } from '../../../../ui/form-components/datepicker-input/datepicker-input.component';
import { FormErrorComponent } from '../../../../ui/form-components/form-error/form-error.component';
import { FileUploadFieldComponent } from '../../../../ui/form-components/file-uploader/file-upload-field/file-upload-field.component';
import { FileUploaderTextComponent } from '../../../../ui/form-components/file-uploader/file-uploader-text/file-uploader-text.component';
import { FileListItemComponent } from '../../../../ui/file-list-item/file-list-item.component';
import { AttachmentLinkPipe } from '../../../../pipes/attachment-link.pipe';

@Component({
  selector: 'app-spousal-waiver-upload-card',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownInputComponent,
    DatepickerInputComponent,
    FormErrorComponent,
    FileUploadFieldComponent,
    FileUploaderTextComponent,
    FileListItemComponent,
    AttachmentLinkPipe
  ],
  templateUrl: './spousal-waiver-upload-card.component.html',
  styleUrls: ['./spousal-waiver-upload-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpousalWaiverUploadCardComponent implements OnInit, OnChanges {
  private typeService = inject(TypeService);

  @Input() formSubmitted: boolean = false;
  @Input() formGroup!: JurisdictionSpousalWaiverFormGroup;
  @Input() existingSpousalWaivers: (SpousalWaiverAttachment | null)[] = [];
  @Output() removeFile = new EventEmitter<SubmissionAttachment>();

  private MAX_WAIVERS_PER_JURISDICTION = 4;

  public waiverTypeDropdowns$ = this.typeService.spousalWaiverTypes$.baseTypeToDropdowns();

  public jurisdictionName$!: Observable<string>;
  public waiverControls$!: Observable<SpousalWaiverEntryFormGroup[]>;
  public canAddWaiver$!: Observable<boolean>;
  public waiverTypeDropdownsByIndex$!: Observable<DropDownItem[][]>;

  public existingFilesAsSubmissionAttachments: (SubmissionAttachment | null)[] = [];

  public customErrorMessages = {
    spousalDeclaration: {
      required: $localize`A file upload is required`
    },
    spousalWaiverDateSigned: {
      required: $localize`Date Signed is required`
    },
    spousalWaiverEffectiveDate: {
      required: $localize`Effective Date is required`
    },
    spousalWaiverType: {
      required: $localize`Waiver Type is required`
    }
  };

  private get waivers(): FormArray<SpousalWaiverEntryFormGroup> {
    return this.formGroup.controls.waivers;
  }

  public ngOnInit(): void {
    this.existingFilesAsSubmissionAttachments = this.mapExistingWaiversToAttachments();

    this.jurisdictionName$ = this.formGroup.controls.governingJurisdictionId.valueChanges.pipe(
      startWith(this.formGroup.controls.governingJurisdictionId.value),
      map(id => (id !== null ? GOVERNING_JURISDICTION_NAMES[id as GoverningJurisdictionTypeId] ?? '' : ''))
    );

    this.waiverControls$ = this.waivers.valueChanges.pipe(
      startWith(null),
      map(() => this.waivers.controls)
    );

    this.canAddWaiver$ = this.waivers.valueChanges.pipe(
      startWith(null),
      map(() => this.waivers.length < this.MAX_WAIVERS_PER_JURISDICTION)
    );

    this.waiverTypeDropdownsByIndex$ = combineLatest([
      this.waiverTypeDropdowns$,
      this.waivers.valueChanges.pipe(startWith(null))
    ]).pipe(map(([dropdowns]) => this.buildWaiverTypeDropdownsByIndex(dropdowns)));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.existingSpousalWaivers) {
      this.existingFilesAsSubmissionAttachments = this.mapExistingWaiversToAttachments();
    }
  }

  public addWaiver(): void {
    if (this.waivers.length < this.MAX_WAIVERS_PER_JURISDICTION) {
      this.waivers.push(buildSpousalWaiverEntryFormGroup());
    }
  }

  public removeWaiver(index: number): void {
    if (this.waivers.length > 1) {
      if (this.waivers.at(index).value.id) {
        this.removeSpousalWaiver(index);
      }
      this.waivers.removeAt(index);
      this.existingFilesAsSubmissionAttachments = this.mapExistingWaiversToAttachments();
    }
  }

  public removeExistingWaiverFile(index: number, file: SubmissionAttachment): void {
    this.removeFile.emit(file);

    if (this.waivers.length > 1) {
      this.waivers.removeAt(index);
    } else {
      this.waivers.at(index).reset();
    }

    this.existingFilesAsSubmissionAttachments = this.mapExistingWaiversToAttachments();
  }

  public trackByWaiver(_index: number, control: SpousalWaiverEntryFormGroup): SpousalWaiverEntryFormGroup {
    return control;
  }

  private buildWaiverTypeDropdownsByIndex(dropdowns: DropDownItem[]): DropDownItem[][] {
    const selectedByIndex = this.waivers.controls.map(entry => entry.controls.spousalWaiverType.value);
    return this.waivers.controls.map((_entry, index) => {
      const selectedElsewhere = new Set(selectedByIndex.filter((value, i) => i !== index && value !== null));
      return dropdowns.map(
        dropdown =>
          new DropDownItem(
            dropdown.title,
            dropdown.value,
            dropdown.disabled || selectedElsewhere.has(dropdown.value),
            dropdown.iconSrc
          )
      );
    });
  }

  private mapExistingWaiversToAttachments(): (SubmissionAttachment | null)[] {
    return this.waivers.controls.map(entry => this.mapExistingWaiverToAttachment(entry));
  }

  private mapExistingWaiverToAttachment(entry: SpousalWaiverEntryFormGroup): SubmissionAttachment | null {
    const waiverType = entry.controls.spousalWaiverType.value;
    const existing =
      waiverType !== null
        ? this.existingSpousalWaivers?.find(waiver => waiver?.waiverTypeId === waiverType) ?? null
        : null;
    if (!existing?.attachment) return null;

    const attachment = Object.assign(new Attachment(), existing.attachment);
    return {
      id: existing.id?.toString() ?? '',
      attachment,
      uploadedDuringSubmission: true
    } as SubmissionAttachment;
  }

  private removeSpousalWaiver(index: number): void {
    const attachment = this.mapExistingWaiverToAttachment(this.waivers.at(index));
    if (attachment) this.removeFile.emit(attachment);
  }
}
